import type { ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, HostListener, ViewChild } from '@angular/core';

import { ResizableModule, type ResizeEvent } from 'angular-resizable-element';
import { type SelectedFileExtended } from '@common/types/file.type';

import {
    type BeforeAfterWidgetData,
    EHandleOrientation,
} from '@web/widgets/before-after/type/before-after-widget.type';
import { EResizeType } from '@web/widgets/image/type/image-widget.type';
import { BLANK_IMAGE } from '@web/widgets/before-after/constants/before-after.consts';
import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';
import { formatFileUrl, formatFileUrlWithResize } from '@components/sp-file-manager/src/helpers';
import { SpDirectivesModule } from '@common/directives/directives.module';

import { environment } from '@web-env/environment';
import { getStyles } from './before-after-view.component.styles';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'web-before-after-view',
    templateUrl: './before-after-view.component.html',
    styleUrls: ['./before-after-view.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [CommonModule, ResizableModule, SpDirectivesModule],
})
export class BeforeAfterViewComponent extends WebBaseViewComponent<BeforeAfterWidgetData> {
    @ViewChild('resizeBox') resizeBox: ElementRef<HTMLElement>;
    @ViewChild('beforeImageRef') beforeImageRef: ElementRef<HTMLElement>;
    @ViewChild('afterImageRef') afterImageRef: ElementRef<HTMLElement>;

    public EResizeType = EResizeType;
    public EHandleOrientation = EHandleOrientation;

    protected getStyles = getStyles;

    private isDragging = false;

    public get beforeImage(): SelectedFileExtended {
        return this.widgetData.beforeImage;
    }

    public get afterImage(): SelectedFileExtended {
        return this.widgetData.afterImage;
    }

    public get beforeImgSrc(): string {
        if (!this.beforeImage) {
            return BLANK_IMAGE;
        }

        return formatFileUrlWithResize(this.beforeImage, environment.fileManagerCdnUrl, 720);
    }

    public get beforeImgFallbackSrc(): string {
        if (!this.beforeImage) {
            return BLANK_IMAGE;
        }

        return formatFileUrl(this.beforeImage, environment.fileManagerCdnUrl);
    }

    public get afterImgSrc(): string {
        if (!this.afterImage) {
            return BLANK_IMAGE;
        }

        return formatFileUrlWithResize(this.afterImage, environment.fileManagerCdnUrl, 720);
    }

    public get afterImgFallbackSrc(): string {
        if (!this.afterImage) {
            return BLANK_IMAGE;
        }

        return formatFileUrl(this.afterImage, environment.fileManagerCdnUrl);
    }

    public startDragging(event: MouseEvent | TouchEvent): void {
        event.preventDefault();
        this.isDragging = true;

        if (event instanceof MouseEvent) {
            document.addEventListener('mousemove', this.handleDrag);
            document.addEventListener('mouseup', this.stopDragging);
        } else if (event instanceof TouchEvent) {
            document.addEventListener('touchmove', this.handleDrag);
            document.addEventListener('touchend', this.stopDragging);
        }
    }

    @HostListener('document:mousemove', ['$event'])
    @HostListener('document:touchmove', ['$event'])
    private handleDrag = (event: MouseEvent | TouchEvent): void => {
        if (!this.isDragging) return;

        const container = this.resizeBox.nativeElement;
        const rect = container.getBoundingClientRect();

        let clientX: number;
        let clientY: number;

        if (event instanceof MouseEvent) {
            clientX = event.clientX;
            clientY = event.clientY;
        } else if (event instanceof TouchEvent) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        }

        let percentage: number;

        if (this.widgetData.handleOrientation === EHandleOrientation.horizontal) {
            const y = clientY - rect.top;
            percentage = (y / rect.height) * 100;
        } else {
            const x = clientX - rect.left;
            percentage = (x / rect.width) * 100;
        }

        percentage = Math.round(percentage / 5) * 5;
        percentage = Math.max(5, Math.min(95, percentage));

        this.updateWidgetData({ handlePosition: percentage });
        this.changeDetectorRef.detectChanges();
    };

    @HostListener('document:mouseup')
    @HostListener('document:touchend')
    private stopDragging = (): void => {
        if (!this.isDragging) return;
        this.isDragging = false;
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.stopDragging);
        document.removeEventListener('touchmove', this.handleDrag);
        document.removeEventListener('touchend', this.stopDragging);
    };

    public onResizeStart(event: ResizeEvent, target: HTMLElement): void {}

    public onResizing(event: ResizeEvent, target: HTMLElement): void {
        if (typeof event.edges.bottom !== 'number') return;
        if (event.rectangle.width <= 50) return;

        const newPadding = (event.rectangle.height / event.rectangle.width) * 100;
        target.style.paddingTop = newPadding + '%';
    }

    public onResizeEnd(event: ResizeEvent, target: HTMLElement): void {
        this.updateWidgetData({
            beforeImage: {
                ...this.widgetData.beforeImage,
                resizedHeight: event.rectangle.height,
                resizedWidth: event.rectangle.width,
            },
            afterImage: {
                ...this.widgetData.afterImage,
                resizedHeight: event.rectangle.height,
                resizedWidth: event.rectangle.width,
            },
        });
    }

    public getBeforeImagePaddingTop(image: SelectedFileExtended): number {
        if (!image) {
            return 20;
        }

        if (!image?.resizedHeight || !image?.resizedWidth) {
            return 50;
        }

        return (image.resizedHeight / image.resizedWidth) * 100;
    }

    public overlayImageSrc(image: SelectedFileExtended): string | boolean {
        if (!image?.overlayImage) {
            return false;
        }
        return formatFileUrl(image.overlayImage, environment.fileManagerCdnUrl);
    }

    public getAfterImageClipStyle(): { [key: string]: string } {
        const { handleOrientation } = this.widgetData;

        if (handleOrientation === EHandleOrientation.horizontal) {
            return {
                clipPath: `inset(${this.widgetData.handlePosition}% 0 0 0)`,
            };
        } else {
            return {
                clipPath: `inset(0 0 0 ${this.widgetData.handlePosition}%)`,
            };
        }
    }

    public getSliderHandleStyle(): { [key: string]: string } {
        const { handleOrientation } = this.widgetData;

        if (handleOrientation === EHandleOrientation.horizontal) {
            return {
                top: this.widgetData.handlePosition ? `${this.widgetData.handlePosition}%` : '50%',
                left: '50%',
            };
        } else {
            return {
                left: this.widgetData.handlePosition ? `${this.widgetData.handlePosition}%` : '50%',
                top: '50%',
            };
        }
    }

    public getSliderBorderStyles(): { [key: string]: string } {
        const { handleOrientation } = this.widgetData;

        if (handleOrientation === EHandleOrientation.horizontal) {
            return {
                transform: 'translate(0%, -50%)',
                top: this.widgetData.handlePosition ? `${this.widgetData.handlePosition}%` : '50%',
                left: '0',
                right: '0',
                width: '100%',
                height: '4px',
            };
        } else {
            return {
                transform: 'translate(-50%, 0%)',
                left: this.widgetData.handlePosition ? `${this.widgetData.handlePosition}%` : '50%',
                top: '0',
                bottom: '0',
                width: '4px',
                height: '100%',
            };
        }
    }

    imagePosition = { x: 50, y: 50 };
    cursorPosBefore = { x: 0, y: 0 };
    imagePosBefore = null;
    imagePosAfter = null;
    isMouseDown = false;

    mouseDown(event: MouseEvent, image: SelectedFileExtended): void {
        event.preventDefault();
        this.imagePosition = {
            x: image.backgroundPositionX || 50,
            y: image.backgroundPositionY || 50,
        };
        this.isMouseDown = true;
        this.imagePosAfter = this.imagePosition;
        this.imagePosBefore = this.imagePosAfter;
        this.cursorPosBefore = { x: event.pageX, y: event.pageY };
    }

    mouseUp(type: 'before' | 'after'): void {
        if (!this.isMouseDown) return;
        this.isMouseDown = false;

        if (type === 'before') {
            this.updateWidgetData({
                beforeImage: {
                    ...this.widgetData.beforeImage,
                    backgroundPositionX: this.imagePosAfter.x,
                    backgroundPositionY: this.imagePosAfter.y,
                },
            });
        } else {
            this.updateWidgetData({
                afterImage: {
                    ...this.widgetData.afterImage,
                    backgroundPositionX: this.imagePosAfter.x,
                    backgroundPositionY: this.imagePosAfter.y,
                },
            });
        }
    }

    move(event: MouseEvent, imageRef: HTMLImageElement): void {
        event.preventDefault();
        if (event.buttons !== 1 || !this.isMouseDown) return;

        const newXPos = this.imagePosBefore.x + ((this.cursorPosBefore.x - event.clientX) / imageRef.width) * 100;
        const newYPos = this.imagePosBefore.y + ((this.cursorPosBefore.y - event.clientY) / imageRef.height) * 100;

        if (newXPos < 0 || newXPos > 100 || newYPos < 0 || newYPos > 100) {
            return;
        }
        this.imagePosAfter = { x: newXPos, y: newYPos };

        (event.target as HTMLImageElement).style.objectPosition = `${newXPos}% ${newYPos}%`;
    }
}
